import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { InventoryItem } from '../../utils/types';
import { useCart } from '../../contexts/CartContext';
import { formatCurrency } from '../../utils/formatters';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';

// استخدام مسار نسبي للصورة الافتراضية
const placeholderImage = '/assets/images/product-placeholder.png';

interface ProductCardProps {
  product: InventoryItem;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addToCart, isInCart } = useCart();
  const { settings } = useStoreSettings();
  const [quickViewOpen, setQuickViewOpen] = useState(false);

  const cardStyle = settings?.display?.productCardStyle || 'classic';
  const cardSize = settings?.display?.productCardSize || 'medium';
  const showQuickView = settings?.display?.showQuickView !== false;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
  };

  // تحديد الارتفاع بناءً على حجم الكارت المختار
  let imageHeight = { xs: 130, sm: 160, md: 190 };
  if (cardSize === 'small') {
    imageHeight = { xs: 100, sm: 130, md: 150 };
  } else if (cardSize === 'large') {
    imageHeight = { xs: 160, sm: 200, md: 240 };
  }

  // تحديد ستايل الكارت بناءً على التنسيق المختار
  let sxStyles: any = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)'
    }
  };

  switch (cardStyle) {
    case 'modern-shadow':
      sxStyles = {
        ...sxStyles,
        borderRadius: '16px',
        border: 'none',
        boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
        '&:hover': {
          ...sxStyles['&:hover'],
          boxShadow: '0 20px 40px rgba(0,0,0,0.12)'
        }
      };
      break;
    case 'minimal':
      sxStyles = {
        ...sxStyles,
        borderRadius: 0,
        border: 'none',
        boxShadow: 'none',
        background: 'transparent',
        '&:hover': {
          ...sxStyles['&:hover'],
          transform: 'translateY(-2px)',
          boxShadow: 'none'
        }
      };
      break;
    case 'bordered':
      sxStyles = {
        ...sxStyles,
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        '&:hover': {
          ...sxStyles['&:hover'],
          borderColor: 'primary.main',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
        }
      };
      break;
    case 'classic':
    default:
      sxStyles = {
        ...sxStyles,
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        '&:hover': {
          ...sxStyles['&:hover'],
          boxShadow: '0 8px 16px rgba(0,0,0,0.12)'
        }
      };
      break;
  }

  return (
    <>
      <Card className="product-card" sx={sxStyles}>
        <CardActionArea
          component={Link}
          to={`/product/${product.id}`}
          sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
        >
          <CardMedia
            component="img"
            image={product.imageUrl || placeholderImage}
            alt={product.name}
            sx={{
              objectFit: 'contain',
              p: { xs: 0.5, sm: 1.5 },
              height: imageHeight
            }}
          />
          <CardContent sx={{ flexGrow: 1, p: { xs: 1, sm: 1.5 }, '&:last-child': { pb: { xs: 1, sm: 1.5 } } }}>
            <Typography
              gutterBottom
              component="div"
              noWrap
              sx={{ fontSize: { xs: '0.78rem', sm: '0.95rem', md: '1rem' }, fontWeight: 600, mb: 0.5 }}
            >
              {product.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 0.5,
                height: { xs: '28px', sm: '36px' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: { xs: '0.68rem', sm: '0.8rem' },
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {product.description || 'لا يوجد وصف متاح'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: { xs: 0.5, sm: 1 } }}>
              <Typography
                color="primary"
                fontWeight="bold"
                sx={{ fontSize: { xs: '0.82rem', sm: '1rem', md: '1.1rem' } }}
              >
                {formatCurrency(product.sellingPrice)}
              </Typography>
              {product.quantity <= 5 && (
                <Chip
                  label={product.quantity > 0 ? `${product.quantity}` : 'نفذ'}
                  color={product.quantity > 0 ? 'warning' : 'error'}
                  size="small"
                  sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, height: { xs: 18, sm: 22 } }}
                />
              )}
            </Box>
          </CardContent>
        </CardActionArea>
        <CardActions sx={{ p: { xs: '4px 6px', sm: '8px' }, display: 'flex', gap: 0.5 }}>
          <Button
            color="primary"
            variant="contained"
            fullWidth
            startIcon={<ShoppingCartIcon sx={{ fontSize: { xs: '0.85rem', sm: '1.1rem' } }} />}
            onClick={handleAddToCart}
            disabled={product.quantity <= 0 || isInCart(product.id)}
            sx={{
              fontSize: { xs: '0.65rem', sm: '0.8rem' },
              py: { xs: 0.4, sm: 0.75 },
              minHeight: { xs: 28, sm: 36 }
            }}
          >
            {isInCart(product.id) ? 'في السلة' : 'أضف للسلة'}
          </Button>

          {showQuickView && (
            <IconButton
              color="secondary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setQuickViewOpen(true);
              }}
              sx={{
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
                p: { xs: 0.5, sm: 1 },
                borderRadius: '8px'
              }}
            >
              <VisibilityIcon sx={{ fontSize: { xs: '1rem', sm: '1.3rem' } }} />
            </IconButton>
          )}
        </CardActions>
      </Card>

      {/* نافذة المعاينة السريعة (Quick View Dialog) */}
      <Dialog
        open={quickViewOpen}
        onClose={() => setQuickViewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'Cairo' }}>
            معاينة سريعة للمنتج
          </Typography>
          <IconButton onClick={() => setQuickViewOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ dir: 'rtl', p: 1 }}>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box
                component="img"
                src={product.imageUrl || placeholderImage}
                alt={product.name}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '300px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'right' }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontFamily: 'Cairo', mb: 2 }}>
                {product.name}
              </Typography>
              <Typography variant="h5" color="primary" fontWeight="bold" sx={{ mb: 2 }}>
                {formatCurrency(product.sellingPrice)}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
                {product.description || 'لا يوجد وصف متاح لهذا المنتج.'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>التصنيف:</strong> {product.category || 'غير محدد'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                <strong>حالة المخزون:</strong> {product.quantity > 0 ? `متوفر (${product.quantity} قطعة)` : 'غير متوفر'}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<ShoppingCartIcon />}
                onClick={(e) => {
                  handleAddToCart(e);
                  setQuickViewOpen(false);
                }}
                disabled={product.quantity <= 0 || isInCart(product.id)}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  borderRadius: '8px'
                }}
                fullWidth
              >
                {isInCart(product.id) ? 'في السلة' : 'أضف للسلة'}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setQuickViewOpen(false)} color="inherit" sx={{ fontWeight: 'bold' }}>
            إغلاق المعاينة
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProductCard;
